import { FallbackError } from "@core/domain/errors/fallback.error";
import type { ModelError } from "@iot-platform/core";
import { StationDatasource } from "@station/data/datasources/station.datasource";
import type { LatestData } from "@station/domain/models/latest-data.model";
import type { Station } from "@station/domain/models/station.model";
import type { StationRepository } from "@station/domain/repositories/station.repository";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { BehaviorSubject, Observable } from "rxjs";

@injectable()
export class StationImplRepository implements StationRepository {
    constructor(
        @inject(StationDatasource)
        private readonly stationDatasource: StationDatasource,
    ) {}

    private _selectedStations: BehaviorSubject<Readonly<Station>[]> =
        new BehaviorSubject<Readonly<Station>[]>([]);

    get selectedStations(): Observable<readonly Readonly<Station>[]> {
        return this._selectedStations;
    }

    addStation(station: Readonly<Station> | Readonly<Station>[]): void {
        const currentStations = this._selectedStations.value;

        const stationsToAdd = Array.isArray(station) ? station : [station];

        const newStations = stationsToAdd.filter(
            (stationVal) =>
                !currentStations.some(
                    (currentS) =>
                        currentS.id.valueOrThrow === stationVal.id.valueOrThrow,
                ),
        );
        this._selectedStations.next([...currentStations, ...newStations]);
    }

    removeStation(
        station: Readonly<Station> | Readonly<Station>[] | number,
    ): void {
        if (Array.isArray(station)) {
            this._selectedStations.next(
                this._selectedStations.value.filter(
                    (stationVal) =>
                        !station.some(
                            (removed) =>
                                removed.id.value === stationVal.id.value,
                        ),
                ),
            );
        } else if (typeof station === "number") {
            this._selectedStations.next(
                this._selectedStations.value.filter(
                    (_stationVal, i) => i !== station,
                ),
            );
        } else {
            this._selectedStations.next(
                this._selectedStations.value.filter(
                    (stationVal) => stationVal.id.value !== station.id.value,
                ),
            );
        }
    }

    removeAllStations(): void {
        this._selectedStations.next([]);
    }

    latestData(
        stationId: string,
    ): ResultAsync<
        LatestData[],
        ModelError<typeof LatestData> | FallbackError
    > {
        return this.stationDatasource.latestData(stationId);
    }

    getStationBy(
        stationId: number,
    ): ResultAsync<Station, ModelError<typeof Station> | FallbackError> {
        return this.stationDatasource.getStationBy(stationId);
    }
}
